import React, { useContext, useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';
import { deleteClient, getClientUsage, searchPersons } from 'app/api/clientApi';
import { ToastContext } from 'app/context/ToastContext';

interface Props {
  client: any;
  visible: boolean;
  onHide: () => void;
  onDeleted: () => void;
}

const DeleteClientDialog: React.FC<Props> = ({ client, visible, onHide, onDeleted }) => {
  const { showSuccess, showError } = useContext(ToastContext);
  const [usage, setUsage] = useState<{ policyHolderCount: number; insuredClientCount: number; nfaCardCount: number } | null>(null);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [filter, setFilter] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [replacement, setReplacement] = useState<any>(null);

  useEffect(() => {
    if (visible && client?.id) {
      setUsage(null);
      setReplacement(null);
      setFilter('');
      setSearchResults([]);
      setLoadingUsage(true);
      getClientUsage(client.id)
        .then(r => setUsage(r.data))
        .catch(e => {
          console.error(e);
          showError('Chyba', e.response?.data?.detail);
          onHide();
        })
        .finally(() => setLoadingUsage(false));
    }
  }, [visible, client?.id]);

  useEffect(() => {
    if (!visible || !filter) {
      return;
    }
    const handle = setTimeout(() => {
      setSearching(true);
      searchPersons('?filter=' + encodeURIComponent(filter))
        .then(r => setSearchResults((r.data.content || []).filter(c => c.id !== client?.id)))
        .catch(e => console.error(e))
        .finally(() => setSearching(false));
    }, 500);
    return () => clearTimeout(handle);
  }, [filter, visible]);

  const hasLinks = usage && (usage.policyHolderCount > 0 || usage.insuredClientCount > 0 || usage.nfaCardCount > 0);
  const canConfirm = usage && (!hasLinks || replacement);

  const handleDelete = () => {
    setDeleting(true);
    deleteClient(client.id, hasLinks ? replacement?.id : null)
      .then(() => {
        showSuccess('Úspech', 'Klient bol zmazaný.');
        onDeleted();
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', e.response?.data?.detail);
      })
      .finally(() => setDeleting(false));
  };

  const selectButtonTemplate = (rowData: any) => (
    <Button label="Vybrať" type="button" style={{ backgroundColor: '#e31818' }} onClick={() => setReplacement(rowData)} />
  );

  const footer = (
    <div>
      <Button style={{ backgroundColor: 'gray' }} label="Zrušiť" icon="pi pi-times" outlined onClick={onHide} disabled={deleting} />
      <Button
        style={{ backgroundColor: '#e31818' }}
        label="Vymazať"
        icon="pi pi-check"
        severity="danger"
        onClick={handleDelete}
        disabled={!canConfirm || deleting}
      />
    </div>
  );

  return (
    <Dialog
      header="Vymazať klienta"
      visible={visible}
      style={{ width: '70rem' }}
      breakpoints={{ '960px': '85vw', '641px': '95vw' }}
      modal
      footer={footer}
      onHide={onHide}
    >
      {loadingUsage && (
        <div className="flex justify-content-center">
          <ProgressSpinner />
        </div>
      )}
      {!loadingUsage && usage && (
        <div>
          <div className="confirmation-content" style={{ marginBottom: '1rem' }}>
            <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
            <span>
              Naozaj chcete vymazať klienta <b>{client?.nameFullFormatted || client?.id}</b>?
            </span>
          </div>

          {hasLinks && (
            <>
              <div style={{ marginBottom: '1rem' }}>
                Klient má naviazané záznamy:
                <ul>
                  {usage.policyHolderCount > 0 && <li>{usage.policyHolderCount} zmlúv ako poistník</li>}
                  {usage.insuredClientCount > 0 && <li>{usage.insuredClientCount} zmlúv ako poistený</li>}
                  {usage.nfaCardCount > 0 && <li>{usage.nfaCardCount} NFA kariet</li>}
                </ul>
                Vyberte náhradného klienta, na ktorého sa všetky tieto väzby presunú:
              </div>

              {replacement ? (
                <div style={{ padding: '10px', border: '1px solid #ccc', borderRadius: '8px', marginBottom: '1rem' }}>
                  <div style={{ fontWeight: 'bold' }}>{replacement.nameFullFormatted}</div>
                  <div>{replacement.birthNr}</div>
                  <div>{replacement.email}</div>
                  <Button
                    label="Zmeniť"
                    type="button"
                    size="small"
                    style={{ backgroundColor: 'gray', marginTop: '5px' }}
                    onClick={() => setReplacement(null)}
                  />
                </div>
              ) : (
                <>
                  <div className="p-field" style={{ width: '100%' }}>
                    <InputText
                      style={{ width: '100%' }}
                      value={filter}
                      onChange={e => setFilter(e.target.value)}
                      placeholder="Hľadať náhradného klienta"
                    />
                  </div>
                  {searchResults.length > 0 && (
                    <DataTable
                      stripedRows
                      loading={searching}
                      size="small"
                      value={searchResults}
                      responsiveLayout="scroll"
                      className="p-mt-4"
                    >
                      <Column field="nameFullFormatted" header="Meno" />
                      <Column field="birthNr" header="Rodné číslo" />
                      <Column field="ico" header="IČO" />
                      <Column field="email" header="E-mail" />
                      <Column field="phone" header="Telefón" />
                      <Column body={selectButtonTemplate} header="Akcia" />
                    </DataTable>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}
    </Dialog>
  );
};

export default DeleteClientDialog;
